import axios from "axios";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { DateTime } from "luxon";

import AdherenceChart from "../../components/adherence-chart/adherence-chart.component";
import { Card, EmptyState, PageSpinner } from "../../components/ui";
import { ChevronRightIcon, ClockIcon } from "../../components/ui/icons";
import { adherenceOver, dailySeries, groupEvents } from "../../utilities/adherence";
import { eventType } from "../../utilities/notifications";

const RANGES = [
  { days: 7, label: "7 days" },
  { days: 14, label: "14 days" },
];

const TONE_RAIL = {
  good: "border-teal-500",
  warning: "border-amber-500",
  critical: "border-red-500",
  neutral: "border-gray-300",
};

const TONE_TEXT = {
  good: "text-teal-600",
  warning: "text-amber-600",
  critical: "text-red-600",
  neutral: "text-gray-500",
};

/**
 * Summary view over /adherence: the trend chart, a window total, and the most
 * recent events. The full, filterable log lives on the Notifications page.
 */
const Adherence = () => {
  const [adherence, setAdherence] = useState(null);
  const [range, setRange] = useState(7);

  const { deviceId } = useParams();
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`/app-api/device/${deviceId}/adherence`);
      if (data) setAdherence(data);
    } catch (err) {
      console.error(err);
    }
  }, [deviceId]);

  useEffect(() => {
    load();
  }, [load]);

  if (!adherence) return <PageSpinner />;

  const zone = adherence.deviceTimezone?.timezoneValue || DateTime.local().zoneName;
  const days = adherence.days || [];
  const summary = adherenceOver(days, range);
  const series = dailySeries(days, range, zone);
  const log = groupEvents(days, zone, 3);
  const available = Math.min(days.length, 14);

  return (
    <>
      <div className="mb-5 grid gap-5 lg:grid-cols-3 lg:items-start">
        <Card className="p-5 lg:col-span-2 nav:p-6">
          <div className="mb-4 flex items-center justify-between gap-3">
            <p className="text-sm font-semibold uppercase tracking-wide text-gray-500">Daily adherence</p>
            <div className="flex items-center gap-1 rounded-lg bg-gray-100 p-0.5">
              {RANGES.filter((r) => r.days <= available).map((r) => (
                <button
                  key={r.days}
                  onClick={() => setRange(r.days)}
                  className={`rounded-[6px] px-3 py-1 text-xs font-semibold transition ${
                    range === r.days ? "bg-white text-primary-800 shadow-sm" : "text-gray-500"
                  }`}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>
          <AdherenceChart series={series} />
        </Card>

        <Card className="p-5 nav:p-6">
          <p className="mb-1 text-xs text-gray-500">{range}-Day Adherence</p>
          <p className="font-mono text-4xl font-bold text-primary-700">
            {summary.pct === null ? "—" : `${summary.pct}%`}
          </p>
          <p className="mt-2 text-[11px] text-gray-500">
            {summary.taken} of {summary.total} doses taken · {summary.missed} missed
          </p>
        </Card>
      </div>

      <Card className="p-5 nav:p-6">
        <div className="mb-4 flex items-center justify-between gap-4">
          <p className="text-sm font-semibold uppercase tracking-wide text-gray-500">Recent events</p>
          <button
            onClick={() => navigate(`/device/${deviceId}/notifications`)}
            className="flex items-center gap-1 text-xs font-semibold text-primary-600"
          >
            View all
            <ChevronRightIcon className="h-3.5 w-3.5" />
          </button>
        </div>

        {log.length === 0 ? (
          <EmptyState icon={ClockIcon} title="No events yet" description="Dose events will appear here once the device starts reporting." />
        ) : (
          log.map((day) => (
            <div key={day.date} className="mb-5 last:mb-0">
              <p className="mb-2 text-xs font-semibold text-primary-800">{day.heading}</p>
              <div className="space-y-2">
                {day.events.map((event) => {
                  const type = eventType(event.notification);
                  return (
                    <div key={event.id} className={`border-l-2 py-1 pl-4 ${TONE_RAIL[type.tone]}`}>
                      <p className="text-sm text-gray-700">{event.message}</p>
                      <p className="text-[11px] text-gray-500">
                        {event.time}
                        <span className={`ml-2 font-medium ${TONE_TEXT[type.tone]}`}>{type.label}</span>
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </Card>
    </>
  );
};

export default Adherence;
